import { isDeepEqual } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjsUtils from '@attain-apps/rxjs-utils'
import { PartialId, Word } from '@attain-apps/string-builder'
import { TypeChecker } from '@attain-apps/type-guards'
import * as rxjs from 'rxjs'

import type * as me from '../../../me'
import type { Feature } from '../feature'
import * as gql from '../gql'
import * as models from '../models'

export const onApproveProposalRequest = store.effects.create(
  (feature: Feature, meFeature: me.Feature) =>
    feature.actions.approveProposal.request.observe().pipe(
      rxjsUtils.latestFrom(action => {
        const currentChartId = models.Chart.Id.from(action.data.widgetRefId)
        const proposedChartId = models.Chart.Id.from({
          entry: 'proposed',
          widgetRefId: action.data.widgetRefId,
        })
        return {
          action: rxjs.of(action),
          context: meFeature.contexts.selectors.zoneContext.asObservable,
          currentChart: feature.selectors.getChart({ chartId: currentChartId })
            .asObservable,
          proposedChart: feature.selectors.getChart({
            chartId: proposedChartId,
          }).asObservable,
        }
      }),
      rxjs.switchMap(({ action, context, currentChart, proposedChart }) => {
        if (!context) {
          return rxjs.of(
            feature.actions.approveProposal.cancellation.from(action).create({
              reason: 'Context not available',
              request: action.data,
            }),
          )
        }

        if (!currentChart || !proposedChart) {
          return rxjs.of(
            feature.actions.approveProposal.cancellation.from(action).create({
              reason: 'Current or proposed chart is not available',
              request: action.data,
            }),
          )
        }

        return rxjs
          .defer(() =>
            _persistApprovedProposal({
              context,
              currentChart,
              proposedChart,
            }),
          )
          .pipe(
            rxjs.map(response =>
              feature.actions.approveProposal.success
                .from(action)
                .create({ request: action.data, response }),
            ),
            rxjs.catchError((error: unknown) =>
              rxjs.of(
                feature.actions.approveProposal.error.from(action).create({
                  error:
                    error instanceof Error ? error : new Error(String(error)),
                  request: action.data,
                }),
              ),
            ),
          )
      }),
    ),
)

function _persistApprovedProposal({
  context,
  currentChart,
  proposedChart,
}: {
  context: ents.core.admin.Context.ForZone
  currentChart: models.Chart
  proposedChart: models.Chart
}): rxjs.Observable<models.ApproveProposal.Response> {
  const { addedOrEditedNodes, deletedNodes, proposedNodesById } = _getNodeDiff({
    currentNodes: currentChart.nodes,
    proposedNodes: proposedChart.nodes,
  })

  const currentPrimaryParentByChild = _getPrimaryParentByChild(
    currentChart.edges,
  )
  const proposedPrimaryParentByChild = _getPrimaryParentByChild(
    proposedChart.edges,
  )

  const nodesToUpsert = new Map<
    ents.core.display.tree.Node.Id,
    ents.core.display.tree.Node
  >()
  for (const node of addedOrEditedNodes) {
    nodesToUpsert.set(ents.core.display.tree.Node.Id.get(node), node)
  }

  const edgesFrom =
    proposedChart.metadata.edgesFrom ?? currentChart.metadata.edgesFrom
  if (TypeChecker.isNullish(edgesFrom)) {
    const changedParentNodeIds = new Set<ents.core.display.tree.Node.Id>([
      ...currentPrimaryParentByChild.keys(),
      ...proposedPrimaryParentByChild.keys(),
    ])

    for (const nodeId of changedParentNodeIds) {
      if (
        currentPrimaryParentByChild.get(nodeId) ===
        proposedPrimaryParentByChild.get(nodeId)
      ) {
        continue
      }
      const node = proposedNodesById.get(nodeId)
      if (node) {
        nodesToUpsert.set(nodeId, node)
      }
    }
  }

  const upsertNodes = Array.from(nodesToUpsert.values())
  const approvedEdges =
    edgesFrom && typeof edgesFrom === 'object'
      ? _getApprovedEdges({
          currentEdges: currentChart.edges,
          proposedEdges: proposedChart.edges,
        })
      : proposedChart.edges

  type MutationOperation = () => rxjs.Observable<unknown>
  let deleteEdgesOperation: MutationOperation | undefined
  let upsertEdgesOperation: MutationOperation | undefined
  let deleteNodesOperation: MutationOperation | undefined
  let upsertNodesOperation: MutationOperation | undefined

  if (edgesFrom && typeof edgesFrom === 'object') {
    const { deletedEdges, upsertEdges } = _getEdgeDiff({
      currentEdges: currentChart.edges,
      proposedEdges: approvedEdges,
    })

    if (deletedEdges.length) {
      const deletedEdgeRecordIds = deletedEdges.map(edge => edge.recordId)
      if (deletedEdgeRecordIds.some(id => !id)) {
        throw new Error(
          'Unable to approve proposal: at least one deleted edge has no recordId for backend deletion.',
        )
      }

      const edgeDeleteModelId = _assertSingleModelId({
        message:
          'Unable to approve proposal: deleted edges are not all from one modelId.',
        modelIds: deletedEdges.map(() => edgesFrom.association),
      })

      deleteEdgesOperation = () =>
        gql.mutations.deleteTreeEditorData.executor({
          ...context,
          deletion: {
            ids: deletedEdgeRecordIds as ents.core.dms.data.Record.Id[],
            modelId: edgeDeleteModelId,
          },
        })
    }

    if (upsertEdges.length) {
      const associationRecords = upsertEdges.map(edge => {
        const childNode = proposedNodesById.get(edge.childId)
        const parentNode = proposedNodesById.get(edge.parentId)

        if (!childNode || !parentNode) {
          throw new Error(
            'Unable to approve proposal: association edge references missing node(s).',
          )
        }

        const edgeRecordId = edge.recordId ?? PartialId.from(Word.generate())
        const leaderType =
          edge.leaderType ?? ents.extra.common.LeaderType.Manager
        const childNodeDisplayName = _getNodeDisplayName(childNode)
        const parentNodeDisplayName = _getNodeDisplayName(parentNode)
        const values: ents.core.dms.data.Record.Values = {
          [ents.core.dms.data.Record.Id.KEY]: edgeRecordId,
          [edgesFrom.child]: {
            entityId: childNode.modelId,
            recordId: edge.childId,
          },
          [edgesFrom.parent]: {
            entityId: parentNode.modelId,
            recordId: edge.parentId,
          },
          '!code': edgeRecordId,
          '!leader_type': leaderType,
          '!name': `${childNodeDisplayName} -> ${parentNodeDisplayName}`,
        }

        const record: ents.core.dms.data.Record = {
          purposes: [],
          values,
        }

        return JSON.stringify(record)
      })

      const edgeUpsertModelId = _assertSingleModelId({
        message:
          'Unable to approve proposal: created/updated edges must all share one modelId.',
        modelIds: upsertEdges.map(() => edgesFrom.association),
      })

      upsertEdgesOperation = () =>
        gql.mutations.putTreeEditorData.executor({
          ...context,
          insertion: {
            inputs: {
              records: associationRecords,
            },
            modelId: edgeUpsertModelId,
          },
        })
    }
  }

  if (deletedNodes.length) {
    const deleteNodeModelId = _assertSingleModelId({
      message:
        'Unable to approve proposal: deleted nodes are not all from one modelId.',
      modelIds: deletedNodes.map(node => node.modelId),
    })

    deleteNodesOperation = () =>
      gql.mutations.deleteTreeEditorData.executor({
        ...context,
        deletion: {
          ids: deletedNodes.map(node =>
            ents.core.display.tree.Node.Id.get(node),
          ),
          modelId: deleteNodeModelId,
        },
      })
  }

  if (upsertNodes.length) {
    const upsertNodeModelId = _assertSingleModelId({
      message:
        'Unable to approve proposal: created/updated nodes must all share one modelId.',
      modelIds: upsertNodes.map(node => node.modelId),
    })

    const nodeRecords = upsertNodes.map(node => {
      const nodeId = ents.core.display.tree.Node.Id.get(node)
      const values = node.values
      if (!values) {
        throw new Error(
          `Unable to approve proposal: node '${nodeId}' has no values.`,
        )
      }

      const record: ents.core.dms.data.Record = {
        ...(TypeChecker.isNullish(edgesFrom)
          ? { parentId: proposedPrimaryParentByChild.get(nodeId) }
          : {}),
        purposes: [],
        values,
      }

      return JSON.stringify(record)
    })

    upsertNodesOperation = () =>
      gql.mutations.putTreeEditorData.executor({
        ...context,
        insertion: {
          inputs: {
            records: nodeRecords,
          },
          modelId: upsertNodeModelId,
        },
      })
  }

  const operations = [
    upsertNodesOperation,
    upsertEdgesOperation,
    deleteEdgesOperation,
    deleteNodesOperation,
  ].filter(TypeChecker.isNotNullish.raw)

  if (operations.length === 0) {
    return rxjs.of({ approvedEdges })
  }

  return rxjs
    .concat(...operations.map(operation => rxjs.defer(operation)))
    .pipe(
      rxjs.last(),
      rxjs.map(() => ({ approvedEdges })),
    )
}

function _getNodeDiff({
  currentNodes,
  proposedNodes,
}: {
  currentNodes: readonly ents.core.display.tree.Node[]
  proposedNodes: readonly ents.core.display.tree.Node[]
}): {
  addedOrEditedNodes: readonly ents.core.display.tree.Node[]
  deletedNodes: readonly ents.core.display.tree.Node[]
  proposedNodesById: ReadonlyMap<
    ents.core.display.tree.Node.Id,
    ents.core.display.tree.Node
  >
} {
  const currentNodesById = _getNodeByIdLookup(currentNodes)
  const proposedNodesById = _getNodeByIdLookup(proposedNodes)

  const addedOrEditedNodes: ents.core.display.tree.Node[] = []
  for (const proposedNode of proposedNodes) {
    const nodeId = ents.core.display.tree.Node.Id.get(proposedNode)
    const currentNode = currentNodesById.get(nodeId)
    if (!currentNode || !isDeepEqual(currentNode, proposedNode)) {
      addedOrEditedNodes.push(proposedNode)
    }
  }

  const deletedNodes: ents.core.display.tree.Node[] = []
  for (const currentNode of currentNodes) {
    const nodeId = ents.core.display.tree.Node.Id.get(currentNode)
    if (!proposedNodesById.has(nodeId)) {
      deletedNodes.push(currentNode)
    }
  }

  return {
    addedOrEditedNodes,
    deletedNodes,
    proposedNodesById,
  }
}

function _getNodeByIdLookup(
  nodes: readonly ents.core.display.tree.Node[],
): ReadonlyMap<ents.core.display.tree.Node.Id, ents.core.display.tree.Node> {
  const lookup = new Map<
    ents.core.display.tree.Node.Id,
    ents.core.display.tree.Node
  >()
  for (const node of nodes) {
    lookup.set(ents.core.display.tree.Node.Id.get(node), node)
  }
  return lookup
}

function _getEdgeDiff({
  currentEdges,
  proposedEdges,
}: {
  currentEdges: readonly models.Edge[]
  proposedEdges: readonly models.Edge[]
}): {
  deletedEdges: readonly models.Edge[]
  upsertEdges: readonly models.Edge[]
} {
  const currentByKey = _getEdgeByKeyLookup(currentEdges)
  const proposedByKey = _getEdgeByKeyLookup(proposedEdges)

  const upsertEdges: models.Edge[] = []
  const deletedEdges: models.Edge[] = []

  for (const [key, proposedEdge] of proposedByKey.entries()) {
    const currentEdge = currentByKey.get(key)
    if (!currentEdge) {
      upsertEdges.push(proposedEdge)
      continue
    }

    if (_getLeaderType(currentEdge) !== _getLeaderType(proposedEdge)) {
      upsertEdges.push({
        ...proposedEdge,
        recordId: proposedEdge.recordId ?? currentEdge.recordId,
      })
    }
  }

  for (const [key, currentEdge] of currentByKey.entries()) {
    if (!proposedByKey.has(key)) {
      deletedEdges.push(currentEdge)
    }
  }

  return {
    deletedEdges,
    upsertEdges,
  }
}

function _getEdgeByKeyLookup(
  edges: readonly models.Edge[],
): ReadonlyMap<string, models.Edge> {
  const lookup = new Map<string, models.Edge>()
  for (const edge of edges) {
    lookup.set(_edgeKey(edge), edge)
  }
  return lookup
}

function _getApprovedEdges({
  currentEdges,
  proposedEdges,
}: {
  currentEdges: readonly models.Edge[]
  proposedEdges: readonly models.Edge[]
}): readonly models.Edge[] {
  const currentByKey = _getEdgeByKeyLookup(currentEdges)

  return proposedEdges.map(proposedEdge => {
    if (proposedEdge.recordId) {
      return proposedEdge
    }

    const currentEdge = currentByKey.get(_edgeKey(proposedEdge))
    if (currentEdge?.recordId) {
      return {
        ...proposedEdge,
        recordId: currentEdge.recordId,
      }
    }

    if (currentEdge) {
      return proposedEdge
    }

    return {
      ...proposedEdge,
      recordId: PartialId.from(Word.generate()),
    }
  })
}

function _getNodeDisplayName(node: ents.core.display.tree.Node): string {
  const nodeName = node.values?.['!name']
  if (TypeChecker.isString(nodeName)) {
    const trimmed = nodeName.trim()
    if (trimmed) {
      return trimmed
    }
  }
  return ents.core.display.tree.Node.Id.get(node)
}

function _edgeKey(edge: Pick<models.Edge, 'childId' | 'parentId'>): string {
  return `${edge.parentId}::${edge.childId}`
}

/**
 * Interprets omitted edge leaderType values as primary manager relationships.
 */
function _getLeaderType(
  edge: Pick<models.Edge, 'leaderType'>,
): ents.extra.common.LeaderType {
  return edge.leaderType ?? ents.extra.common.LeaderType.Manager
}

/**
 * Returns the primary manager parent per child while ignoring secondary leader
 * relationships.
 */
function _getPrimaryParentByChild(
  edges: readonly models.Edge[],
): ReadonlyMap<ents.core.display.tree.Node.Id, ents.core.display.tree.Node.Id> {
  const lookup = new Map<
    ents.core.display.tree.Node.Id,
    ents.core.display.tree.Node.Id
  >()

  for (const edge of edges) {
    if (_getLeaderType(edge) !== ents.extra.common.LeaderType.Manager) {
      continue
    }

    lookup.set(edge.childId, edge.parentId)
  }

  return lookup
}

function _assertSingleModelId<
  TModelId extends ents.core.dms.models.ForRecord.Id,
>(params: { modelIds: readonly TModelId[]; message: string }): TModelId {
  const unique = Array.from(new Set<TModelId>(params.modelIds))
  if (unique.length !== 1) {
    throw new Error(params.message)
  }
  return unique[0]
}
